import {
  MarketplaceKeywordKeysetPosition,
  MarketplaceKeywordResolvedSort,
  MarketplaceKeywordSortValue,
} from '@agilelab/plugin-wb-search-common';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';
import { fromByteArray, toByteArray } from 'base64-js';
import { z } from 'zod';
import type { FilterClause } from './schema';

export const marketplaceSearchSelectionPaths = [
  'external_id',
  'document',
  'document.documentId',
  'document._computedInfo',
  'document.useCaseTemplateId',
  'document.taxonomy_id',
  'document.kind',
  'document._businessConcepts',
] as const;

export type MarketplaceSearchSelectionPath =
  (typeof marketplaceSearchSelectionPaths)[number];

export type EffectiveSelection = MarketplaceSearchSelectionPath[];

export const defaultMarketplaceSearchSelection: EffectiveSelection = [
  'document',
];

const selectionPathOrder = new Map<string, number>(
  marketplaceSearchSelectionPaths.map((path, index) => [path, index]),
);

export function normalizeSelectedPaths(
  paths: readonly string[],
): EffectiveSelection {
  return [...new Set(paths)]
    .filter((path): path is MarketplaceSearchSelectionPath =>
      selectionPathOrder.has(path),
    )
    .sort(
      (left, right) =>
        selectionPathOrder.get(left)! - selectionPathOrder.get(right)!,
    );
}

export function normalizeEffectiveSelection(
  paths?: readonly string[],
): EffectiveSelection {
  const normalized = normalizeSelectedPaths(
    paths ?? defaultMarketplaceSearchSelection,
  );
  if (!normalized.includes('document')) {
    return normalized;
  }
  return normalized.filter(
    path => path === 'document' || !path.startsWith('document.'),
  );
}

const searchCursorVersion = 1;

const cursorPositionZod = z
  .object({
    sortValue: z.union([
      z.string(),
      z.number().finite(),
      z.boolean(),
      z.null(),
    ]),
    kind: z.enum(['system', 'component']),
    urn: z.string().min(1),
  })
  .strict();

const searchCursorPayloadZod = z
  .object({
    version: z.number().int().nonnegative(),
    issuedAt: z.number().int().nonnegative(),
    expiresAt: z.number().int().nonnegative(),
    requestHash: z.string().regex(/^[a-f0-9]{64}$/),
    sortFingerprint: z.string().min(1),
    position: cursorPositionZod,
  })
  .strict()
  .refine(payload => payload.expiresAt >= payload.issuedAt);

export interface CanonicalSearchContext {
  mode: 'keyword' | 'semantic';
  environment: string;
  query: string;
  filters: FilterClause[];
  category: 'summary' | 'complete' | 'selected';
  limit: number;
  selection: EffectiveSelection;
  useHighlight: boolean;
  sort: MarketplaceKeywordResolvedSort;
  sortFingerprint: string;
  requestHash: string;
}

export interface SearchCursorPayload {
  version: number;
  issuedAt: number;
  expiresAt: number;
  requestHash: string;
  sortFingerprint: string;
  position: MarketplaceKeywordKeysetPosition;
}

export interface SearchContextInput {
  mode: 'keyword' | 'semantic';
  environment: string;
  query: string;
  filters: readonly FilterClause[];
  category: 'summary' | 'complete' | 'selected';
  limit: number;
  selection?: readonly string[];
  useHighlight?: boolean;
  sort: MarketplaceKeywordResolvedSort;
}

function stringToUtf8Bytes(value: string): Uint8Array {
  const encoded = encodeURIComponent(value);
  const bytes: number[] = [];
  for (let index = 0; index < encoded.length; index += 1) {
    if (encoded[index] === '%') {
      bytes.push(Number.parseInt(encoded.slice(index + 1, index + 3), 16));
      index += 2;
    } else {
      bytes.push(encoded.charCodeAt(index));
    }
  }
  return Uint8Array.from(bytes);
}

function utf8BytesToString(bytes: Uint8Array): string {
  return decodeURIComponent(
    [...bytes].map(byte => `%${byte.toString(16).padStart(2, '0')}`).join(''),
  );
}

function canonicalizeFilterValues(
  values: readonly (string | boolean)[],
  ignoreCase: boolean,
): Array<string | boolean> {
  const canonicalValues = values.map(value =>
    ignoreCase && typeof value === 'string'
      ? value.toLocaleLowerCase('en-US')
      : value,
  );
  return [
    ...new Map(
      canonicalValues.map(value => [`${typeof value}:${String(value)}`, value]),
    ).values(),
  ].sort((left, right) =>
    `${typeof left}:${String(left)}`.localeCompare(
      `${typeof right}:${String(right)}`,
    ),
  );
}

function canonicalizeFilters(filters: readonly FilterClause[]): FilterClause[] {
  const canonical = filters.map(filter => ({
    id: filter.id,
    operator: filter.operator,
    values: canonicalizeFilterValues(
      filter.values,
      filter.operator === 'inIgnoreCase' ||
        filter.operator === 'notInIgnoreCase',
    ),
  }));
  const unique = new Map(
    canonical.map(filter => [JSON.stringify(filter), filter]),
  );
  return [...unique.values()].sort((left, right) =>
    JSON.stringify(left).localeCompare(JSON.stringify(right)),
  );
}

function hashCanonicalValue(value: unknown): string {
  return bytesToHex(sha256(stringToUtf8Bytes(JSON.stringify(value))));
}

/** Builds the deterministic request identity to which continuations are bound. */
export async function computeCanonicalSearchContext(
  input: SearchContextInput,
): Promise<CanonicalSearchContext> {
  const filters = canonicalizeFilters(input.filters);
  const selection =
    input.category === 'selected'
      ? normalizeEffectiveSelection(input.selection)
      : [];
  const useHighlight =
    input.category === 'selected' && input.useHighlight === true;
  const sort = { ...input.sort };
  const hashInput = {
    mode: input.mode,
    environment: input.environment,
    query: input.query.trim(),
    filters,
    category: input.category,
    limit: input.limit,
    sort: {
      id: sort.id,
      direction: sort.direction,
      field: sort.field ?? null,
      valueType: sort.valueType,
      nulls: sort.nulls,
      fingerprint: sort.fingerprint,
      includeRank: sort.includeRank,
    },
    selection,
    useHighlight,
  };

  return {
    ...hashInput,
    sort,
    sortFingerprint: sort.fingerprint,
    requestHash: hashCanonicalValue(hashInput),
  };
}

/** Encodes a cursor payload as a URL-safe token. */
export function encodeSearchCursor(payload: SearchCursorPayload): string {
  return fromByteArray(stringToUtf8Bytes(JSON.stringify(payload)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

/** Decodes an untrusted token without exposing its contents in failures. */
export function decodeSearchCursor(cursor: string): SearchCursorPayload {
  try {
    if (!/^[A-Za-z0-9_-]+$/.test(cursor) || cursor.length % 4 === 1) {
      throw new Error();
    }
    const base64 = cursor.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const bytes = toByteArray(padded);
    const canonicalCursor = fromByteArray(bytes)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/g, '');
    if (canonicalCursor !== cursor) throw new Error();

    const parsed = searchCursorPayloadZod.safeParse(
      JSON.parse(utf8BytesToString(bytes)),
    );
    if (!parsed.success) throw new Error();
    return parsed.data;
  } catch {
    throw new Error('Failed to decode cursor');
  }
}

/** Creates an expiring cursor bound to one canonical request and sort. */
export function createSearchCursorPayload(
  position: MarketplaceKeywordKeysetPosition,
  context: CanonicalSearchContext,
  ttlMs: number,
  now = Date.now(),
): SearchCursorPayload {
  const payload: SearchCursorPayload = {
    version: searchCursorVersion,
    issuedAt: now,
    expiresAt: now + ttlMs,
    requestHash: context.requestHash,
    sortFingerprint: context.sortFingerprint,
    position: { ...position },
  };
  const parsed = searchCursorPayloadZod.safeParse(payload);
  if (!parsed.success) throw new Error('Invalid cursor state');
  return parsed.data;
}

export function isTypedSortValue(
  value: MarketplaceKeywordSortValue,
  valueType: MarketplaceKeywordResolvedSort['valueType'],
): boolean {
  if (value === null) return true;
  if (valueType === 'relevance' || valueType === 'number') {
    return typeof value === 'number' && Number.isFinite(value);
  }
  if (valueType === 'boolean') return typeof value === 'boolean';
  if (valueType === 'date') {
    return typeof value === 'string' && !Number.isNaN(Date.parse(value));
  }
  return typeof value === 'string';
}

/** Validates decoded cursor state against the current canonical request. */
export function validateSearchCursor(
  payload: SearchCursorPayload,
  context: CanonicalSearchContext,
  now = Date.now(),
): true {
  const parsed = searchCursorPayloadZod.safeParse(payload);
  if (!parsed.success) throw new Error('Cursor malformed');
  if (parsed.data.version !== searchCursorVersion) {
    throw new Error('Cursor version unsupported');
  }
  if (parsed.data.expiresAt < now) throw new Error('Cursor expired');
  if (parsed.data.requestHash !== context.requestHash) {
    throw new Error('Cursor request mismatch');
  }
  if (parsed.data.sortFingerprint !== context.sortFingerprint) {
    throw new Error('Cursor sort mismatch');
  }
  if (
    !isTypedSortValue(parsed.data.position.sortValue, context.sort.valueType)
  ) {
    throw new Error('Cursor position invalid');
  }
  return true;
}
